import React from 'react';
import { Box, Stack } from '@mui/material';
import { Skeleton } from '@repo/ui';

import { NAV_HEADER_HEIGHT } from './NavigationHeader';

import { getEmbeddedKey } from 'utils/embeddedKey';

/**
 * Loading placeholder for the whole certificate page (VRG-240).
 *
 * This is the hand-off target for the scan loader: the CSS-only loader painted by
 * the entry document covers the gap before this bundle exists, and once the app can
 * see the page's shape this takes over. Per the skeleton standing rule (§19) the
 * placeholder must occupy the SAME footprint the loaded content will, so the swap
 * causes no layout shift — hence the 412px media block, the title/sku/status stack,
 * and the section rows below, mirroring `ImageCarousel` + `ProductPageHeader` +
 * the info sections in CertificatePage.
 *
 * Deliberately NOT a spinner. The previous behaviour was the opposite of this rule:
 * a full-viewport branded animation held for a fixed 3.8s regardless of load time.
 */
export const CertificatePageSkeleton: React.FC = () => (
  <Stack aria-busy='true' aria-label='Loading certificate' sx={{ width: '100%' }}>
    {/* The brand bar's footprint — `CertificateBrandHeader`, same
        `NAV_HEADER_HEIGHT`. Without it the photograph starts at y=0 here and 72px
        down once loaded, so the whole page would settle downward on the swap,
        which is the one thing §19 exists to prevent.

        Gated only on the embed, because that is the only one of the bar's three
        conditions this component can answer: the certificate has not arrived, so
        its company is unknown, and the view type is unknown too.

        That leaves a known residual — the OWNER view, where `NavigationHeader`
        takes the slot with a negative margin and costs nothing, so a cold owner
        load settles 72px. Taken deliberately: the skeleton's dominant case is a
        cold PUBLIC load (someone has just tapped a tag, which is the product),
        whereas an owner reaches this page from their own list with the data
        already in hand. Parity is spent where the load actually happens. */}
    {!getEmbeddedKey() && (
      <Box sx={{ height: `${NAV_HEADER_HEIGHT}px`, display: 'grid', placeItems: 'center' }}>
        <Skeleton variant='rounded' width={120} height={20} />
      </Box>
    )}

    {/* Image carousel — matches ImageCarousel's 412px main area + dot row */}
    <Skeleton variant='rectangular' width='100%' height={412} />
    <Stack direction='row' spacing={1} justifyContent='center' sx={{ py: 2 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} variant='circular' width={6} height={6} />
      ))}
    </Stack>

    {/* Header — product title, sku, status chip */}
    <Stack spacing={1.5} sx={{ px: 2, pt: 1 }}>
      <Skeleton width='72%' height={22} />
      <Skeleton width='45%' height={14} />
      <Skeleton variant='rounded' width={78} height={28} />
    </Stack>

    {/* Primary action */}
    <Box sx={{ px: 2, pt: 3 }}>
      <Skeleton variant='rounded' width='100%' height={40} />
    </Box>

    {/* Info sections — a heading plus a couple of rows, twice */}
    {Array.from({ length: 2 }).map((_, section) => (
      <Stack key={section} spacing={2} sx={{ px: 2, pt: 4 }}>
        <Skeleton width='30%' height={16} />
        {Array.from({ length: 3 }).map((_, row) => (
          <Stack
            key={row}
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            spacing={2}
          >
            <Skeleton width='38%' height={13} />
            <Skeleton width='42%' height={13} />
          </Stack>
        ))}
      </Stack>
    ))}
  </Stack>
);

export default CertificatePageSkeleton;
